import React, { createContext, useContext, useState, ReactNode } from 'react';
import { SCREEN_TYPE } from '../util/util';

interface SceneConfig {
    currentScene?: string | null;
    nextScene?: string | null;
}

interface ConfigurationState {
    mediaName: string;
    accountId: string;
    currentMediaId: string;
    currentConfigId: string;
    videoDownloadUrl: string;
    videoHoverUrl: string;
    isMediaLive: boolean;
    reactionUrl: string;
    mediaType: string;
    mediasData: any[];
    sceneConfig: SceneConfig;
    scenario: any;
    screenType: string;
    shouldGetDataIndividually: boolean;
    isPollExpanded: boolean;
    chapters: any[];
    restrictedToken: string | null;
    isMaintainProportions: boolean;
    relatedMediasOfScene: Record<string, any>;
    openSharingModal: boolean;
    openSettingMenu: any;
    shouldChangeCurrentMedia: boolean;
    changeSceneType: string;
    playlistId: string;
    scenePath: any[];
    isTransitionLayerEnable: boolean;
    // The scene's video ended and it is moving to another scene: every widget on screen exits first.
    isSceneEndExiting: boolean;
    transitionType: string;
    isTransitionEffectActive: boolean;
    showCountdownModule: boolean;
    isTransitionLayerFadeOutAnimateActive: boolean;
    embedShareSettings: Record<string, any>;
    isChatActivated: boolean;
    isCloseIncomingMessage: boolean;
    restrictedMedias: any[];
    errorDetails: any[];
    transcriptions: Record<string, any>;
    showTranscriptionsModule: boolean;
    subtitleLanguage: Record<string, any>;
    minimizedPlayer: boolean;
    playerWidth: number;
    viewerEngagementModuleActivated?: boolean;
    thumbnailUrl: string;
    configParams: Record<string, any>;
    isFullScreen: boolean;
    liveTranscriptLanguage: any;
    enabledLiveTranscript: boolean;
    isUserSeekedPlayer: boolean;
    disableFocus: boolean;
    countdownStartTime: string | undefined;
    showRegistrationForm: boolean | undefined;
    hasPublishedEngagement: boolean;
}

interface ConfigurationContextType extends ConfigurationState {
    setPollExpanded: (_expanded: boolean) => void;
    setScreenType: (_type: string) => void;
    setScenario: (_scenario: any) => void;
    setMediaType: (_type: string) => void;
    setScenarioThumbnail: (_url: string) => void;
    setSceneConfig: (_config: SceneConfig) => void;
    setMediaName: (_name: string) => void;
    setReactionUrl: (_url: string) => void;
    setVideoDownloadUrl: (_url: string) => void;
    setVideoHoverUrl: (_url: string) => void;
    setAccountId: (_id: string) => void;
    setCurrentMediaId: (_id: string) => void;
    setCurrentPlaylistId: (_id: string) => void;
    setMediasData: (_data: any[]) => void;
    setRelatedMediaOfScene: (_data: Record<string, any>) => void;
    setGetDataIndividually: (_value: boolean) => void;
    setCurrentConfigId: (_id: string) => void;
    setIsMediaLive: (_live: boolean) => void;
    setChapters: (_chapters: any[]) => void;
    setRestrictedToken: (_token: any | null) => void;
    setIsMaintainProportions: (_maintain: boolean) => void;
    setOpenSharingModal: (_open: boolean) => void;
    setOpenSettingMenu: (_menu: any) => void;
    setShouldChangeCurrentMedia: (_should: boolean) => void;
    setChangeSceneType: (_type: string) => void;
    setViewerEngagementModuleActivated: (_activated: boolean) => void;
    setChatActivated: (_activated: boolean) => void;
    setCloseIncomingMessage: (_close: boolean) => void;
    updateSceneHistory: (_newPath: any) => void;
    setSceneHistory: (_path: any[]) => void;
    setTransitionLayer: (_active: boolean) => void;
    setSceneEndExiting: (_exiting: boolean) => void;
    setTransitionOut: (_type: string) => void;
    setTransitionEffectStatus: (_active: boolean) => void;
    setShowCountdownModule: (_show: boolean) => void;
    setEmbedShareSettings: (_settings: Record<string, any>) => void;
    setRestrictedMedias: (_medias: any[]) => void;
    setTranscriptions: (_transcriptions: Record<string, any>) => void;
    setShowTranscriptionsModule: (_show: boolean) => void;
    setSubtitleLanguage: (_language: Record<string, any>) => void;
    setMinimizedPlayerState: (_minimized: boolean) => void;
    setPlayerWidth: (_width: number) => void;
    setConfigParams: (_params: Record<string, any>) => void;
    setIsFullScreen: (_value: boolean) => void;
    setLiveTranscriptLanguage: (_language: any) => void;
    setEnabledLiveTranscript: (_enabled: boolean) => void;
    setUserSeekedPlayer: (_isUserSeekedPlayer: boolean) => void;
    addTranscription: (_newEntry: any) => void;
    setErrorDetails: (_errorDetails: any[]) => void;
    setDisableFocus: (_disable: boolean) => void;
    setCountdownStartTime: (_time: string | undefined) => void;
    setShowRegistrationForm: (_showRegistrationForm: boolean) => void;
    setHasPublishedEngagement: (_has: boolean) => void;
}

const initialState: ConfigurationState = {
    mediaName: '',
    accountId: '',
    currentMediaId: '',
    currentConfigId: '',
    videoDownloadUrl: '',
    videoHoverUrl: '',
    isMediaLive: false,
    reactionUrl: '',
    mediaType: 'video',
    mediasData: [],
    sceneConfig: {
        currentScene: null,
        nextScene: null,
    },
    scenario: null,
    screenType: SCREEN_TYPE.huge,
    shouldGetDataIndividually: false,
    isPollExpanded: false,
    chapters: [],
    restrictedToken: null,
    isMaintainProportions: false,
    relatedMediasOfScene: {},
    openSharingModal: false,
    openSettingMenu: undefined,
    shouldChangeCurrentMedia: false,
    changeSceneType: '',
    playlistId: '',
    scenePath: [],
    isTransitionLayerEnable: false,
    isSceneEndExiting: false,
    transitionType: '',
    isTransitionEffectActive: false,
    showCountdownModule: false,
    isTransitionLayerFadeOutAnimateActive: false,
    embedShareSettings: {},
    isChatActivated: true,
    isCloseIncomingMessage: false,
    restrictedMedias: [],
    errorDetails: [],
    transcriptions: {},
    showTranscriptionsModule: false,
    subtitleLanguage: {},
    minimizedPlayer: false,
    playerWidth: 60,
    thumbnailUrl: '',
    configParams: {},
    isFullScreen: false,
    liveTranscriptLanguage: '',
    enabledLiveTranscript: false,
    isUserSeekedPlayer: false,
    disableFocus: false,
    countdownStartTime: undefined,
    showRegistrationForm: undefined,
    hasPublishedEngagement: false
};

const ConfigurationContext = createContext<ConfigurationContextType | undefined>(undefined);

export const ConfigurationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<ConfigurationState>(initialState);

    const setPollExpanded = (isPollExpanded: boolean) => setState((s) => ({ ...s, isPollExpanded }));
    const setScreenType = (screenType: string) => setState((s) => ({ ...s, screenType }));
    const setScenario = (scenario: any) => setState((s) => ({ ...s, scenario }));
    const setMediaType = (mediaType: string) => setState((s) => ({ ...s, mediaType }));
    const setScenarioThumbnail = (thumbnailUrl: string) => setState((s) => ({ ...s, thumbnailUrl }));
    const setSceneConfig = (sceneConfig: SceneConfig) => setState((s) => ({ ...s, sceneConfig }));
    const setMediaName = (mediaName: string) => setState((s) => ({ ...s, mediaName }));
    const setReactionUrl = (reactionUrl: string) => setState((s) => ({ ...s, reactionUrl }));
    const setVideoDownloadUrl = (videoDownloadUrl: string) => setState((s) => ({ ...s, videoDownloadUrl }));
    const setVideoHoverUrl = (videoHoverUrl: string) => setState((s) => ({ ...s, videoHoverUrl }));
    const setAccountId = (accountId: string) => setState((s) => ({ ...s, accountId }));
    const setCurrentMediaId = (currentMediaId: string) => setState((s) => ({ ...s, currentMediaId }));
    const setCurrentPlaylistId = (playlistId: string) => setState((s) => ({ ...s, playlistId }));
    const setMediasData = (mediasData: any[]) => setState((s) => ({ ...s, mediasData }));
    const setRelatedMediaOfScene = (relatedMediasOfScene: Record<string, any>) => setState((s) => ({ ...s, relatedMediasOfScene }));
    const setGetDataIndividually = (shouldGetDataIndividually: boolean) => setState((s) => ({ ...s, shouldGetDataIndividually }));
    const setCurrentConfigId = (currentConfigId: string) => setState((s) => ({ ...s, currentConfigId }));
    const setIsMediaLive = (isMediaLive: boolean) => setState((s) => ({ ...s, isMediaLive }));
    const setChapters = (chapters: any[]) => setState((s) => ({ ...s, chapters }));
    const setRestrictedToken = (restrictedToken: any | null) => setState((s) => ({ ...s, restrictedToken }));
    const setIsMaintainProportions = (isMaintainProportions: boolean) => setState((s) => ({ ...s, isMaintainProportions }));
    const setOpenSharingModal = (openSharingModal: boolean) => setState((s) => ({ ...s, openSharingModal }));
    const setOpenSettingMenu = (openSettingMenu: any) => setState((s) => ({ ...s, openSettingMenu }));
    const setShouldChangeCurrentMedia = (shouldChangeCurrentMedia: boolean) => setState((s) => ({ ...s, shouldChangeCurrentMedia }));
    const setChangeSceneType = (changeSceneType: string) => setState((s) => ({ ...s, changeSceneType }));
    const setViewerEngagementModuleActivated = (viewerEngagementModuleActivated: boolean) => setState((s) => ({ ...s, viewerEngagementModuleActivated }));
    const setChatActivated = (isChatActivated: boolean) => setState((s) => ({ ...s, isChatActivated }));
    const setCloseIncomingMessage = (isCloseIncomingMessage: boolean) => setState((s) => ({ ...s, isCloseIncomingMessage }));
    const updateSceneHistory = (newPath: any) => setState((s) => ({ ...s, scenePath: [...s.scenePath, newPath] }));
    const setSceneHistory = (scenePath: any[]) => setState((s) => ({ ...s, scenePath }));
    const setTransitionLayer = (isActive: boolean) => setState((s) => ({ ...s, isTransitionLayerEnable: isActive }));
    const setSceneEndExiting = (isSceneEndExiting: boolean) => setState((s) => ({ ...s, isSceneEndExiting }));
    const setTransitionOut = (transitionType: string) => setState((s) => ({ ...s, transitionType }));
    const setTransitionEffectStatus = (isActive: boolean) => setState((s) => ({ ...s, isTransitionEffectActive: isActive }));
    const setShowCountdownModule = (showCountdownModule: boolean) => setState((s) => ({ ...s, showCountdownModule }));
    const setEmbedShareSettings = (embedShareSettings: Record<string, any>) => setState((s) => ({ ...s, embedShareSettings }));
    const setRestrictedMedias = (restrictedMedias: any[]) => setState((s) => ({ ...s, restrictedMedias }));
    const setTranscriptions = (transcriptions: Record<string, any>) => setState((s) => ({ ...s, transcriptions }));
    const setShowTranscriptionsModule = (showTranscriptionsModule: boolean) => setState((s) => ({ ...s, showTranscriptionsModule }));
    const setSubtitleLanguage = (subtitleLanguage: Record<string, any>) => setState((s) => ({ ...s, subtitleLanguage }));
    const setMinimizedPlayerState = (minimizedPlayer: boolean) => setState((s) => ({ ...s, minimizedPlayer }));
    const setPlayerWidth = (playerWidth: number) => setState((s) => ({ ...s, playerWidth }));
    const setConfigParams = (configParams: Record<string, any>) => setState((s) => ({ ...s, configParams }));
    const setIsFullScreen = (isFullScreen: boolean) => setState((s) => ({ ...s, isFullScreen }));
    const setLiveTranscriptLanguage = (liveTranscriptLanguage: any) => setState((s) => ({ ...s, liveTranscriptLanguage }));
    const setEnabledLiveTranscript = (enabledLiveTranscript: boolean) => setState((s) => ({ ...s, enabledLiveTranscript }));
    const setUserSeekedPlayer = (isUserSeekedPlayer: boolean) => setState((s) => ({ ...s, isUserSeekedPlayer }));
    const addTranscription = (newEntry: any) => {
        setState((state) => {
            const existingEntries = state.transcriptions.entries || [];

            // Check if entry already exists (by id, timestamp, or content)
            const isDuplicate = existingEntries.some(
                (entry: any) => entry.content === newEntry.content && entry.endTime === newEntry.endTime && entry.startTime === newEntry.startTime
            );

            if (isDuplicate) {
                return state;
            }
            return {
                ...state,
                transcriptions: {
                    ...state.transcriptions,
                    language: state.transcriptions.language || newEntry.language || 'en',
                    entries: [...existingEntries, newEntry],
                },
            };
        });
    };
    const setHasPublishedEngagement = (hasPublishedEngagement: boolean) => setState((s) => ({ ...s, hasPublishedEngagement }));
    const setErrorDetails = (errorDetails: any[]) => setState((s) => ({ ...s, errorDetails }));
    const setDisableFocus = (disableFocus: boolean) => setState((s) => ({ ...s, disableFocus }));
    const setCountdownStartTime = (countdownStartTime: string | undefined) => setState((s) => ({ ...s, countdownStartTime }));
    const setShowRegistrationForm = (showRegistrationForm: boolean) => setState((s) => {
        if (s.showRegistrationForm === showRegistrationForm) {
            return s;
        }

        return { ...s, showRegistrationForm };
    });

    const value: ConfigurationContextType = {
        ...state,
        setPollExpanded,
        setScreenType,
        setScenario,
        setMediaType,
        setScenarioThumbnail,
        setSceneConfig,
        setMediaName,
        setReactionUrl,
        setVideoDownloadUrl,
        setVideoHoverUrl,
        setAccountId,
        setCurrentMediaId,
        setCurrentPlaylistId,
        setMediasData,
        setRelatedMediaOfScene,
        setGetDataIndividually,
        setCurrentConfigId,
        setIsMediaLive,
        setChapters,
        setRestrictedToken,
        setIsMaintainProportions,
        setOpenSharingModal,
        setOpenSettingMenu,
        setShouldChangeCurrentMedia,
        setChangeSceneType,
        setViewerEngagementModuleActivated,
        setChatActivated,
        setCloseIncomingMessage,
        updateSceneHistory,
        setSceneHistory,
        setTransitionLayer,
        setSceneEndExiting,
        setTransitionOut,
        setTransitionEffectStatus,
        setShowCountdownModule,
        setEmbedShareSettings,
        setRestrictedMedias,
        setTranscriptions,
        setShowTranscriptionsModule,
        setSubtitleLanguage,
        setMinimizedPlayerState,
        setPlayerWidth,
        setConfigParams,
        setIsFullScreen,
        setLiveTranscriptLanguage,
        setEnabledLiveTranscript,
        setUserSeekedPlayer,
        addTranscription,
        setErrorDetails,
        setDisableFocus,
        setCountdownStartTime,
        setShowRegistrationForm,
        setHasPublishedEngagement
    };

    return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

export const useConfiguration = (): ConfigurationContextType => {
    const context = useContext(ConfigurationContext);
    if (!context) {
        throw new Error('useConfiguration must be used within a ConfigurationProvider');
    }
    return context;
};
